import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { ElasticVehiclesResponse } from '@//app/api/search/vehicles';
import { useApiBaseUrl } from '@//providers/MarketSettingsProvider';
import { httpClientGetJson } from '../../httpClient';
import { createSearchParams, type OverrideParams } from '../../utils';

export const overrideVehicleSearchParams: OverrideParams = {
  hits: 24,
};

interface UseVehiclesSettings {
  initialData?: ElasticVehiclesResponse;
}

const useVehicles = (params: URLSearchParams, settings?: UseVehiclesSettings) => {
  const apiBaseUrl = useApiBaseUrl();
  const { queryString } = createSearchParams(params, overrideVehicleSearchParams);

  const [pollInterval, setPollInterval] = useState<number | false>(false);

  useEffect(() => {
    window.pollSearch = (enabled = true) => setPollInterval(enabled ? 60000 : false);

    return () => {
      delete window.pollSearch;
    };
  }, []);

  // initialData is only valid for the queryString the server actually ran. Two
  // shapes of URL change hit this hook:
  //
  //   - pushState (filter toggles): SearchContainer does NOT remount; the
  //     `initialData` prop reference stays frozen at first-mount value while
  //     queryString shifts. The frozen data is stale for the new key.
  //   - Real Next.js navigation (pagination): SearchContainer re-renders with
  //     a NEW `initialData` reference produced by a fresh server render. That
  //     data IS valid for the new queryString.
  //
  // Distinguish by tracking which queryString the current initialData
  // reference belongs to. When the reference changes, the parent has just
  // re-fetched server-side, so it's fresh for the current queryString.
  const [initialDataTracking, setInitialDataTracking] = useState(() => ({
    data: settings?.initialData,
    validFor: queryString,
  }));

  if (settings?.initialData !== initialDataTracking.data) {
    setInitialDataTracking({ data: settings?.initialData, validFor: queryString });
  }

  const initialData =
    initialDataTracking.validFor === queryString ? initialDataTracking.data : undefined;

  // staleTime must exceed Cloudflare's max-age (60s) + stale-while-revalidate (120s),
  // otherwise dehydrated query state baked into cached HTML is considered stale on
  // hydration and triggers a refetch that races with React's hydration pass.
  const query = useQuery<ElasticVehiclesResponse>({
    queryKey: ['vehicles', queryString],
    queryFn: () =>
      httpClientGetJson<ElasticVehiclesResponse>(`${apiBaseUrl}/api/search/vehicles${queryString}`),
    initialData,
    initialDataUpdatedAt: initialData ? Date.now() : undefined,
    staleTime: initialData ? 300_000 : 0,
    refetchOnMount: !initialData,
    placeholderData: (previous) => previous,
    refetchInterval: pollInterval,
  });

  const isPolling = pollInterval !== false;

  return { ...query, isPolling };
};

export default useVehicles;
